/* eslint-disable max-depth */
// Modules
import React, { useEffect, useCallback } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { ErrorResponse } from 'apollo-link-error'

// Application
import { useLoginHook } from 'hooks'
import { Lab, SignInCIPCredentials } from 'typings/auth'
import { isEmpty, singleSPA } from 'utils'
import { OutputErrorCode, ROUTES_PATH, tracking } from 'constants/index'
// Components
import { SelectLaboratoryForm } from '../SelectLaboratoryForm'
import { usePhasedLogin } from 'redesign/hooks/usePhasedLogin'
import { defaultConfig } from 'modules/RemoteConfig/defaultConfig'
import SearchIllustration from 'redesign/assets/svg/search.svg'
import { useRemoteConfig } from 'modules/RemoteConfig/hooks/useRemoteConfig'
import { useCPFLoginForm } from 'redesign/hooks/useCPFLoginForm'

export const SelectLaboratoryWrapper: React.VFC = () => {
    const { phasedLoginVisibility, dispatchAnalytics } = usePhasedLogin()
    const { labs, getLabs, verifyUser, isSubmitting, getRecaptchaToken, authenticateCIP } = useLoginHook()
    const history = useHistory()
    const location = useLocation<SignInCIPCredentials>()
    const { isFeatureEnabled } = useRemoteConfig()
    const { values } = useCPFLoginForm()

    const newRedirectFlow = isFeatureEnabled('AUTH_NEW_REDIRECT_FLOW')

    let redirectLink = `${ROUTES_PATH.FORWARDING_APP}${location.search}`

    if (!newRedirectFlow) {
        const {
            strings: { AUTH_REDIRECT_LOGIN_LIMITED_FLOW },
        } = defaultConfig

        redirectLink = AUTH_REDIRECT_LOGIN_LIMITED_FLOW
    }

    const handleSubmit = async (lab: Lab) => {
        const recaptchaToken = await getRecaptchaToken()
        const payload: SignInCIPCredentials = {
            cip: location.state.cip,
            password: location.state.password,
            dateOfBirth: location.state.dateOfBirth,
            recaptchaToken: recaptchaToken || '',
            lab: lab.name,
            isInvalid: location.state.isInvalid,
        }
        try {
            await authenticateCIP(payload)
            const { isRedirect } = await verifyUser({
                externalStep: phasedLoginVisibility ? 'PHASED_LOGIN' : 'LOGIN',
                loginType: tracking.LOGIN_TYPE.NLAB,
                cip: payload.cip,
                birthDate: payload.dateOfBirth,
            })
            if (!isRedirect) {
                singleSPA.historyPush(redirectLink, {
                    cip: payload.cip,
                })
            }
        } catch (err) {
            const e: ErrorResponse = err
            if (e.graphQLErrors && !isEmpty(e.graphQLErrors)) {
                const codeError = e.graphQLErrors[0].extensions?.code
                const step = values.showPasswordField ? 'insert_password' : 'login_failure'
                dispatchAnalytics(step)
                if (codeError === OutputErrorCode.CIPUNAUTHENTICATED) {
                    history.push(ROUTES_PATH.LOGIN_CIP_ERROR, {
                        buttonRedirectTo: '/login?phased-login-origin=true',
                        description:
                            // eslint-disable-next-line max-len
                            'Não conseguimos validar seu protocolo (CIP). Verifique as informações preenchidas e tente novamente ou tente acessar com CPF.',
                        illustrationImage: SearchIllustration,
                        title: 'Algo está errado',
                    })
                    return
                }
            }
            history.push(ROUTES_PATH.LOGIN_CIP_ERROR, {
                buttonRedirectTo: '/entrar',
                description:
                    // eslint-disable-next-line max-len
                    'Não conseguimos validar seu protocolo (CIP). Verifique as informações preenchidas e tente novamente ou tente acessar com CPF.',
                illustrationImage: SearchIllustration,
                title: 'Algo está errado',
            })
        }
    }

    const getLaboratory = useCallback(async () => {
        try {
            await getLabs()
        } catch {
            singleSPA.historyPush('/erro-de-conexao', { pageContent: 'pageConnectionFailure' })
        }
    }, [getLabs])

    useEffect(() => {
        if (!location.state) {
            history.push('/login')
        } else {
            getLaboratory()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, history])

    if (labs.length === 0 || !location.state) {
        return null
    }

    return <SelectLaboratoryForm handleSubmit={handleSubmit} isSubmitting={isSubmitting} options={labs} />
}
